/* @flow */

import { generatePath as routerGeneratePath } from 'react-router';

import {
  assertNumber,
  assertObject,
  assertString,
} from '@e180/util/lib/assert';

import {
  getEventBraindateSlug,
  getEventSplashSponsors,
} from '@e180/domain/lib/event/util';
import { defaultBraindateSlug } from '@e180/domain/lib/event/setting';
import {
  isMembershipOnboardingCompleted,
  isMembershipOptInCompleted,
} from '@e180/domain/lib/membership/util';
import { isFunction } from '@e180/util/lib/type';

import { getAuthenticationToken } from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';
import {
  isOnboardingCompletedOnDevice,
  isSplashSeen,
} from 'src/shared/app/onboarding/selector/uiOnboardingSelectors';
import { isOptinDone } from 'src/shared/app/permission/util/permissionUtils';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { getMembership } from 'src/shared/domain/membership/selector/membershipSelectors';
import { isMembershipTermsAccepted } from 'src/shared/domain/membership/util/membershipUtil';

import type { RouteData } from 'src/shared/app/base/route/type/routeTypes';
import type { ReactElement } from 'src/shared/core/type/reactTypes';
import type { State } from 'src/shared/core/type/reduxTypes';

export function getRoutePath({ path }: RouteData, state?: State): string {
  if (isFunction(path) && state) {
    // $FlowIssue
    return path(state);
  }

  // $FlowIssue
  return path;
}

export function getRouteTemplate(
  { template }: RouteData,
  state?: State,
  config?: Object,
): ReactElement {
  if (isFunction(template) && state) {
    return template(state, config);
  }

  return template;
}

export function getRouteMain(
  { main }: RouteData,
  state?: State,
  config?: Object,
): ReactElement {
  if (isFunction(main) && state) {
    return main(state, config);
  }

  return main;
}

export function getRouteHelp({ help }: RouteData, state?: State): boolean {
  if (help && isFunction(help) && state) {
    return help(state);
  }

  return !!help;
}

export function getRouteRedirect(
  { redirect }: RouteData,
  state: State,
  config: Object,
): ?string {
  if (redirect) {
    return redirect(state, config);
  }
}

export function getRouteScrollToTop({ scrollToTop }: RouteData): boolean {
  return !!scrollToTop;
}

export function getBraindateSlug(state: State): string {
  return getEventBraindateSlug(getEventSafely(state)) || defaultBraindateSlug;
}

export function isSplashScreenShown(state: State): boolean {
  const splashSponsors = getEventSplashSponsors(getEventSafely(state));
  return (
    !getAuthenticationToken(state) &&
    !isSplashSeen(state) &&
    !!splashSponsors &&
    splashSponsors.length > 0
  );
}

export function isPermissionScreenShown(
  state: State,
  areEmailNotificationsEnabled: boolean,
): boolean {
  const membership = getMembership(state);

  return !!membership && !isOptinDone(membership, areEmailNotificationsEnabled);
}

export function isMarketShown(state: State): boolean {
  const membership = getMembership(state);

  let isOnboardingCompleted = isOnboardingCompletedOnDevice(state);
  let isOptinCompleted;
  let areTermsAccepted;

  if (membership) {
    isOnboardingCompleted = isMembershipOnboardingCompleted(membership);
    isOptinCompleted = isMembershipOptInCompleted(membership);
    areTermsAccepted = isMembershipTermsAccepted(membership);
  }

  return !!(
    getAuthenticationToken(state) &&
    isOnboardingCompleted &&
    isOptinCompleted &&
    areTermsAccepted
  );
}

export function isOnboardingScreenShown(state: State): boolean {
  if (getAuthenticationToken(state)) {
    const membership = getMembership(state);

    if (membership) {
      return (
        !isOnboardingCompletedOnDevice(state) &&
        !isMembershipOnboardingCompleted(membership)
      );
    }

    return true;
  }

  return !isSplashScreenShown(state);
}

export function getPathId(match: Object, name: string | null = 'id'): number {
  assertString(name, 'name');
  return parseInt(match.params[name], 10);
}

/**
 * Add an HTTP status code to the context passed in `props`
 * Cf. https://reacttraining.com/react-router/web/guides/server-rendering/404-401-or-any-other-status
 * @param {Object} props - Object that contains the context
 * @param {number} status - HTTP status code
 * @throws {TypeError} `props` must be an object
 * @throws {TypeError} `status` must be a number
 * @return {undefined}
 */
export function setHttpStatusCode(props: Object, status: number): void {
  assertObject(props, 'props');
  assertNumber(status, 'status');

  const { staticContext } = props;

  if (staticContext && typeof staticContext === 'object') {
    staticContext.status = status;
  }
}

export function generatePath(
  route: RouteData,
  params?: Object,
  state?: State,
): void {
  return routerGeneratePath(getRoutePath(route, state), params);
}
